import { useCallback, useSyncExternalStore } from 'react';

/**
 * Who the person is writing a reply for, picked beside the assistant's composer: the customer on a
 * support ticket, or support roles chosen by hand. Sent with every message so the help-article
 * search reads only what that customer may read. Nothing picked is the team's own view of every
 * article. A choice, not an authority: the server resolves it inside the tenant and refuses what
 * does not resolve.
 *
 * One value for the tab, shared by the docked panel and the full page (they are one
 * conversation), kept in sessionStorage so it survives navigation, and tied to the workspace it
 * was picked in so it never follows the person into another one.
 */
export interface AnswerFor {
  /** The support ticket whose customer the reply is for. */
  ticketId?: string;
  /** Support role keys, when picked by hand. */
  roles?: string[];
  /** What the chip says: "AA-12 · Cleo", "Partner". */
  label: string;
}

/** The part of the choice the server reads. */
export interface AnswerForPayload {
  ticketId?: string;
  roles?: string[];
}

const STORAGE_KEY = 'revvly.agent.answerFor';

interface Stored extends AnswerFor {
  tenantId: string | null;
}

function currentTenantId(): string | null {
  try {
    return localStorage.getItem('selectedTenantId') || sessionStorage.getItem('selectedTenantId');
  } catch {
    return null;
  }
}

function readStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();
let cache: { raw: string | null; value: AnswerFor | null } = { raw: null, value: null };

/** The current choice, or null when it was made in another workspace (or never). */
export function readAnswerFor(): AnswerFor | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    raw = null;
  }
  const tenant = currentTenantId();
  const key = `${tenant}|${raw}`;
  if (cache.raw === key) return cache.value;
  const stored = readStored();
  const value =
    stored && stored.tenantId === tenant && (stored.ticketId || stored.roles?.length)
      ? { ticketId: stored.ticketId, roles: stored.roles, label: stored.label }
      : null;
  cache = { raw: key, value };
  return value;
}

/** What to send with a message: only the fields the server reads, or undefined for the team. */
export function answerForPayload(): AnswerForPayload | undefined {
  const value = readAnswerFor();
  if (!value) return undefined;
  if (value.ticketId) return { ticketId: value.ticketId };
  return value.roles?.length ? { roles: value.roles } : undefined;
}

/** Pick someone to answer for, or null for the team's view of every article. */
export function setAnswerFor(value: AnswerFor | null): void {
  try {
    if (value) {
      const stored: Stored = { ...value, tenantId: currentTenantId() };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable (privacy mode): the choice then lasts no longer than this call.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The choice, kept in step across the panel and the full page. */
export function useAnswerFor(): [AnswerFor | null, (value: AnswerFor | null) => void] {
  const value = useSyncExternalStore(subscribe, readAnswerFor, () => null);
  const set = useCallback((next: AnswerFor | null) => setAnswerFor(next), []);
  return [value, set];
}
